import {
  ActionIcon,
  Button,
  Group,
  Stack,
  TextInput,
  Title,
  Select,
} from "@mantine/core";
import { CirclePlus, Trash } from "tabler-icons-react";
import { merge } from "lodash";
import { useLocation, useParams } from "react-router-dom";
import { useLayoutEffect, useEffect } from "react";
import QueryWrapper from "../../common/components/QueryWrapper";
import { useGetWorkforceQuery } from "./workforce.api";
import { skipToken } from "@reduxjs/toolkit/query";

import Collapsable from "../../common/admin/ui/Collapsable";
import WorkforceBreadcrumbs from "./WorkforceBreadcrumbs";
import WorkforceToolbar from "./WorkforceToolbar";
import PlayerView from "./PlayerView";
import { Workforce } from "./workforce.model";
import {
  defaultWorkforce,
  WorkforceFormProvider,
  useWorkforceForm,
} from "./form-context";
import {
  ADMIN_ORGANIZATION,
  ORGANIZATIONS,
  WORKFORCE_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS,
  WORKFORCE_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES,
} from "_roles";
import { AccessRights } from "common/admin/auth/AccessRights";
import { useOrganization } from "common/admin/auth/hooks";

export default function WorkforceView() {
  const { workforceId } = useParams<{ workforceId: string }>();
  const creation = !workforceId;

  const form = useWorkforceForm({
    initialValues: defaultWorkforce,
  });
  const { setValues, setFieldValue } = form;

  const getWorkforceQuery = useGetWorkforceQuery(workforceId ?? skipToken);
  const [organization] = useOrganization();

  useLayoutEffect(() => {
    const data = getWorkforceQuery.data;
    if (data) {
      setValues(merge({}, defaultWorkforce, data));
    }
  }, [getWorkforceQuery, setValues]);

  const location = useLocation();

  useLayoutEffect(() => {
    const state = location.state as { from?: Workforce } | undefined;
    if (state?.from) {
      setValues(state.from);
      window.history.replaceState(null, "");
    }
  }, [location, setValues]);

  // New useEffect to handle organization for non-admin users on creation
  useEffect(() => {
    if (creation && organization !== ADMIN_ORGANIZATION) {
      setFieldValue("organization", organization ?? undefined); // Convert null to undefined
    }
  }, [creation, organization, setFieldValue]);

  return (
    <QueryWrapper query={getWorkforceQuery}>
      {(workforce) => (
        <WorkforceFormProvider form={form}>
          <div
            className="sticky top-0 z-50 pb-4"
            style={{ backgroundColor: "#E9ECEF" }}
          >
            <Title order={3} align={"center"} m={"xs"}>
              {creation
                ? "Nouvel effectif"
                : workforce.name || `Effectif ${workforce.id}`}
            </Title>
            <WorkforceBreadcrumbs workforce={workforce ?? "new"} />

            <WorkforceToolbar />
          </div>

          <Stack>
            <Group className="grid grid-cols-2">
              {organization === ADMIN_ORGANIZATION && (
                <AccessRights
                  authorizedOrganizations={
                    WORKFORCE_ORGANIZATION_VISIBILITY_AUTHORISED_ORGANIZATIONS
                  }
                  authorizedRoles={
                    WORKFORCE_ORGANIZATION_VISIBILITY_AUTHORISED_ROLES
                  }
                >
                  <Select
                    sx={{ flex: 1 }}
                    label="Organisation"
                    required
                    data={ORGANIZATIONS}
                    {...form.getInputProps("organization")}
                  />
                </AccessRights>
              )}
            </Group>
            <Collapsable title="Paramètres">
              <TextInput
                autoFocus={true}
                required
                sx={{ flex: 1 }}
                label={"Nom"}
                {...form.getInputProps("name")}
              />
            </Collapsable>

            {form.values.players.map((player, idx) => (
              <Collapsable
                key={idx}
                title={`Joueur #${idx + 1}`}
                actions={
                  <ActionIcon
                    color="red"
                    variant="subtle"
                    onClick={() => form.removeListItem("players", idx)}
                  >
                    <Trash size={16} />
                  </ActionIcon>
                }
              >
                <PlayerView playerIdx={idx} form={form} />
              </Collapsable>
            ))}

            <Group position={"center"}>
              <Button
                variant={"subtle"}
                leftIcon={<CirclePlus />}
                onClick={() =>
                  form.insertListItem("players", { name: "", logo: "" })
                }
              >
                Ajouter un joueur
              </Button>
            </Group>
          </Stack>
        </WorkforceFormProvider>
      )}
    </QueryWrapper>
  );
}
